import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPosts, fetchCategories, stripHtml, formatDate } from '../../lib/wordpress';
import type { WPPost, WPCategory } from '../../lib/wordpress';
import CommonHeader from '../../components/feature/CommonHeader';
import CommonFooter from '../../components/feature/CommonFooter';
import SeoHead from '../../components/feature/SeoHead';

const columnJsonLd = [
  {
    '@type': 'CollectionPage',
    '@id': 'https://saiwaidental.jp/moyuk/column/',
    url: 'https://saiwaidental.jp/moyuk/column/',
    name: '歯科コラム | さいわいデンタルクリニック moyuk SAPPORO 札幌',
    description: '矯正・ホワイトニング・歯科治療に関するコラム記事。さいわいデンタルクリニック moyuk SAPPOROが札幌から発信する専門情報。',
    inLanguage: 'ja',
    isPartOf: { '@id': 'https://saiwaidental.jp/moyuk/#website' },
  },
  {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'TOP', item: 'https://saiwaidental.jp/moyuk/' },
      { '@type': 'ListItem', position: 2, name: 'コラム', item: 'https://saiwaidental.jp/moyuk/column/' },
    ],
  },
];

const BRAND = '#3E5F8F';

export default function ColumnList() {
  const [posts, setPosts] = useState<WPPost[]>([]);
  const [categories, setCategories] = useState<WPCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);


  useEffect(() => {
    fetchCategories().then(cats =>
      setCategories(cats.filter(c => c.count > 0).slice(0, 10))
    );
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetchPosts(page, 12, selectedCategory)
      .then(({ posts, totalPages }) => {
        setPosts(posts);
        setTotalPages(totalPages);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [page, selectedCategory]);

  const handleCategory = (id: number | undefined) => {
    setSelectedCategory(id);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="歯科コラム | さいわいデンタルクリニック moyuk SAPPORO 札幌"
        description="マウスピース矯正・ホワイトニングの疑問に医療視点でお答えするコラム集。インビザラインの費用・期間・向き不向き・医療費控除など、判断に役立つ情報を随時更新中。さいわいデンタル札幌。"
        canonical="https://saiwaidental.jp/moyuk/column/"
        ogSiteName="moyuk SAPPORO さいわいデンタル"
        ogLocale="ja_JP"
        twitterCard="summary_large_image"
        jsonLd={columnJsonLd}
      />
      <CommonHeader />

      <main className="pt-28 pb-24 px-6 max-w-5xl mx-auto">
        {/* Page heading */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold font-serif text-gray-900 mb-2">歯科コラム｜さいわいデンタル 札幌</h1>
          <p className="text-gray-500 text-sm">矯正・ホワイトニング・歯科治療に関する専門コラム</p>
        </div>

        {/* Category filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => handleCategory(undefined)}
              className="px-4 py-1.5 rounded-full text-sm font-medium transition-colors"
              style={!selectedCategory ? { backgroundColor: BRAND, color: '#fff' } : { backgroundColor: '#f3f4f6', color: '#4b5563' }}
            >
              すべて
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => handleCategory(cat.id)}
                className="px-4 py-1.5 rounded-full text-sm font-medium transition-colors"
                style={selectedCategory === cat.id ? { backgroundColor: BRAND, color: '#fff' } : { backgroundColor: '#f3f4f6', color: '#4b5563' }}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <p className="text-center text-gray-500 py-20">記事の読み込みに失敗しました。時間をおいて再度お試しください。</p>
        )}

        {/* Skeleton */}
        {loading && (
          <div className="grid md:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-lg overflow-hidden border border-gray-100">
                <div className="bg-gray-100 h-40 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="bg-gray-100 h-3 w-16 rounded animate-pulse" />
                  <div className="bg-gray-100 h-4 rounded animate-pulse" />
                  <div className="bg-gray-100 h-4 w-3/4 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Posts grid */}
        {!loading && !error && (
          <>
            {posts.length === 0 ? (
              <p className="text-center text-gray-400 py-20">記事がありません</p>
            ) : (
              <div className="grid md:grid-cols-3 gap-6">
                {posts.map(post => {
                  const thumbnail = post._embedded?.['wp:featuredmedia']?.[0]?.source_url;
                  const category = post._embedded?.['wp:term']?.[0]?.[0];
                  return (
                    <Link
                      key={post.id}
                      to={`/column/${post.slug}/`}
                      className="group block border border-gray-100 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt={stripHtml(post.title.rendered)}
                          className="w-full h-40 object-cover group-hover:opacity-90 transition-opacity"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-40 bg-gray-50 flex items-center justify-center">
                          <span className="text-gray-300 text-xs">さいわいデンタル</span>
                        </div>
                      )}
                      <div className="p-4">
                        {category && (
                          <span
                            className="inline-block text-xs px-2 py-0.5 rounded mb-2 text-white"
                            style={{ backgroundColor: BRAND }}
                          >
                            {category.name}
                          </span>
                        )}
                        <p className="text-xs text-gray-400 mb-1">{formatDate(post.date)}</p>
                        <h2
                          className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:opacity-70 transition-opacity"
                          dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                        />
                        <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                          {stripHtml(post.excerpt.rendered)}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-3 mt-14">
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1}
                  className="px-5 py-2 text-sm border border-gray-200 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  ← 前へ
                </button>
                <span className="text-sm text-gray-500">{page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === totalPages}
                  className="px-5 py-2 text-sm border border-gray-200 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  次へ →
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <CommonFooter />
    </div>
  );
}
